"use client"

import HomeDetailsWrapperAccordion from "@/features/homeowners/components/home-details-wrapper-accordion"
import { useWorkscopeLabelStyle, WORKSCOPE_LABEL_COLORS } from "../home-details-wrapper-accordion"
import { workScopeStatusOptions, interiorWallMaterialsOptions } from "../../constants/options"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import DateStampedTextarea from "../../../../shared/components/form-inputs/form-date-stamped-textarea-input"
import FormCheckboxInput from "../../../../shared/components/form-inputs/form-checkbox-input"
import FormYesNoInput from "../../../../shared/components/form-inputs/form-yes-no-input"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import WorkScopeStatusSelect from "../work-scope-status-select"
import FormMultiSelectInput from "../../../../shared/components/form-inputs/form-multi-select-input"
import { useClaimedByOptions } from "../../hooks/use-claimed-by-options"
import { useWorkscopeClaimedBySync } from "../../hooks/use-workscope-claimed-by-sync"
import { GoodwillToggleLabel } from "../goodwill-toggle-label"

const goodwillGreenStyle = { color: WORKSCOPE_LABEL_COLORS.goodwill } as const
const BASE_FIELD_NAME = "home.drywallHangingInfo"



function HomeDrywallHangingForm() {
    const goodwillLabelStyle = useWorkscopeLabelStyle(true) ?? goodwillGreenStyle
    const { control, setValue, getValues } = useFormContext<HomeWithHomeOwnerFormType>()
    const { options: claimedByOptions, loading: claimedByLoading } = useClaimedByOptions()
    const goodwillDrywallHangingOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillDrywallHangingOn` })
    
    // Watch status field and claimed by field
    const drywallHanging = useWatch({ control, name: `${BASE_FIELD_NAME}.drywallHanging` })
    const claimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.claimedBy` })
    const goodwillDrywallHanging = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillDrywallHanging` })
    const goodwillDrywallHangingClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillDrywallHangingClaimedBy`,
    })

    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: drywallHanging,
        claimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.claimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillDrywallHanging,
        claimedBy: goodwillDrywallHangingClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillDrywallHangingClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillDrywallHangingOn,
    })

    return (
        <HomeDetailsWrapperAccordion heading="Drywall Hanging" wrapperClassName="xl:grid-cols-6">
            {/* Row 1: Drywall Hanging + Claimed By 50/50 (like Trim Install) */}
            <WorkScopeStatusSelect workScopeSlug="drywall-hanging"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Drywall Hanging" toggleName={`${BASE_FIELD_NAME}.goodwillDrywallHangingOn` as any} control={control} />,
                    wrapperClassName: "xl:col-span-3",
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.drywallHanging`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Claimed By",
                    wrapperClassName: "xl:col-span-3",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.claimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillDrywallHangingOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Drywall Hanging Goodwill", labelStyle: goodwillLabelStyle, wrapperClassName: "xl:col-span-3" }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillDrywallHanging`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Drywall Hanging Goodwill Claimed By", wrapperClassName: "xl:col-span-3" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillDrywallHangingClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            {/* Row 2: What is the interior wall material? + Approximate total square feet + Is there texturing? */}
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "What is the interior wall material?",
                    wrapperClassName: "xl:col-span-2",
                }}
                options={interiorWallMaterialsOptions}
                name={`${BASE_FIELD_NAME}.interiorWallMaterial`}
                control={control}
                placeholder="Select wall materials..."
                searchPlaceholder="Search materials..."
            />
            <FormTextInput
                inputGroupProps={{
                    label: "Approximate total square feet wall covering needed",
                    wrapperClassName: "xl:col-span-2",
                }}
                name={`${BASE_FIELD_NAME}.totalSquareFeet`}
                type="number"
                control={control}
            />
            <FormYesNoInput
                inputGroupProps={{
                    label: "Is there texturing on the existing walls?",
                    wrapperClassName: "xl:col-span-2",
                }}
                name={`${BASE_FIELD_NAME}.hasTexturing`}
                control={control}
            />
            <DateStampedTextarea
                includeUserName
                inputGroupProps={{
                    label: "Give a brief description of the wall covering work needed",
                    wrapperClassName: "xl:col-span-6",
                }}
                rows={4}
                name={`${BASE_FIELD_NAME}.description`}
                control={control}
            />
        </HomeDetailsWrapperAccordion>
    )
}

export default HomeDrywallHangingForm
